'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import type { LinkCloudManufacturer } from '@//app/api/search/vehicles/link-cloud';
import { CategoryLinkCloud } from '@//components/CategoryLinkCloud';
import { Heading } from '@//components/Heading';
import { LinkCloud } from '@//components/LinkCloud';
import useVehicleFacets from '@//data/search/vehicles/useVehicleFacets';
import { facetParamsToPath } from '@//utils/vehicles-search/utils';
import styles from './StartpageModelLinks.module.scss';

/**
 * Constants make the split logic clearer and easier to tweak later on.
 */
const TOP_MANUFACTURERS_LIMIT = 8;
const MAX_MANUFACTURERS_LIMIT = 28;

interface StartpageLinkCloudProps {
  searchPage: string;
  linkCloudList: LinkCloudManufacturer[];
  facets: ElasticVehiclesResponse;
}

/**
 * A memoised component that renders two link‑cloud sections:
 *  • The TOP manufacturers (first 8)
 *  • The remaining manufacturers (up to 28)
 *
 * The component enriches the incoming manufacturer list with live facet counts
 * while keeping computations minimal through `useMemo`.
 */
const StartpageLinkCloud = ({
  searchPage,
  linkCloudList,
  facets: initialFacets,
}: StartpageLinkCloudProps) => {
  const t = useTranslations('pages.startpage.modelLinks');
  const tCommon = useTranslations('other.common');
  // 🔄  Keep facets in sync without forcing a refetch on every mount.
  const facetsQuery = useVehicleFacets({
    initialData: initialFacets,
  });
  const facets = facetsQuery.data;

  /**
   * Combine API facet counts with the static `linkCloudList`.
   * The map ➜ object ➜ lookup avoids repeated `Array.find` calls.
   */
  const enrichedManufacturers = useMemo(() => {
    const manufacturerFacetLookup: Record<string, number> | undefined = facets?.facets
      .find(({ id }) => id === 'manufacturer')
      ?.filters.reduce(
        (acc, { displayName, count }) => {
          acc[displayName] = count;
          return acc;
        },
        {} as Record<string, number>,
      );

    return linkCloudList.map((m) => ({
      ...m,
      count: manufacturerFacetLookup?.[m.displayName] ?? m.count,
    }));
  }, [linkCloudList, facets]);

  /**
   * Pre‑split results so we only slice once.
   */
  const [topManufacturers, otherManufacturers] = useMemo(() => {
    const top = enrichedManufacturers.slice(0, TOP_MANUFACTURERS_LIMIT);
    const other = enrichedManufacturers.slice(TOP_MANUFACTURERS_LIMIT, MAX_MANUFACTURERS_LIMIT);
    return [top, other] as const;
  }, [enrichedManufacturers]);

  /**
   * Convert manufacturer data to props expected by the link cloud components.
   * Wrapping this in useMemo prevents unnecessary array recreations.
   */
  const categoryCloudData = useMemo(
    () =>
      topManufacturers.map((m) => ({
        logo: m.logo,
        heading: m.displayName,
        description: t('results', { count: m.count }),
        href: `${searchPage}/${facetParamsToPath(new URLSearchParams(m.query))}`,
        title: tCommon('seoLinks.query', {
          query: `${m.displayName}`,
        }),
        ariaLabel: tCommon('seoLinks.query', {
          query: `${m.displayName}`,
        }),
        links: m.modelSeries.map(({ query, displayName }) => {
          const params = new URLSearchParams(query);
          params.delete('hits'); // Remove hits to avoid unnecessary data fetching
          const slug = facetParamsToPath(params, facets?.facets);

          const href = slug ? `${searchPage}/${slug}` : `${searchPage}?${query}`;

          return {
            href,
            label: displayName,
            title: tCommon('seoLinks.query', {
              query: `${m.displayName} ${displayName}`,
            }),
            ariaLabel: tCommon('seoLinks.query', {
              query: `${m.displayName} ${displayName}`,
            }),
          };
        }),
      })),
    [topManufacturers, searchPage, facets?.facets, t],
  );

  const otherCloudLinks = useMemo(
    () =>
      otherManufacturers.map((m) => {
        const params = new URLSearchParams(m.query);
        params.delete('hits'); // Remove hits to avoid unnecessary data fetching
        const slug = facetParamsToPath(params, facets?.facets);

        const href = slug ? `${searchPage}/${slug}` : `${searchPage}?${m.query}`;

        return {
          href,
          title: tCommon('seoLinks.query', {
            query: `${m.displayName}`,
          }),
          ariaLabel: tCommon('seoLinks.query', {
            query: `${m.displayName}`,
          }),
          label: m.displayName,
        };
      }),
    [otherManufacturers, searchPage, facets?.facets],
  );

  return (
    <>
      <CategoryLinkCloud
        categories={categoryCloudData}
        headingLevel={3}
        ariaLabel="Popular manufacturers"
        testId="popular-model-links"
      />

      {otherCloudLinks.length > 0 && (
        <nav className={styles.other} aria-labelledby="other-model-links-heading">
          <Heading level={3} size="xl" id="other-model-links-heading">
            {t('otherBrands')}
          </Heading>
          <LinkCloud testId="other-model-links" links={otherCloudLinks} />
        </nav>
      )}
    </>
  );
};

export default StartpageLinkCloud;
